import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import styles from './MobileWizardFrame.module.css';

export interface MobileWizardFrameProps {
  /** Title next to the back arrow („Marke", „Einrichtung"). */
  title: string;
  /** „Schritt 1 von 6"; omitted on pages without progress (Admin M3). */
  stepLabel?: string | undefined;
  /** Number of segments of the 4 px bar (the wizard's step count); 0 hides the bar. */
  segments?: number | undefined;
  /** Segments filled with the accent (1-based: the current step and every step before it). */
  current?: number | undefined;
  /** „‹": omitted when there is nowhere to go back to. */
  onBack?: (() => void) | undefined;
  /** The bottom row (text button + primary with `flex: 1`). */
  footer?: ReactNode | undefined;
  children: ReactNode;
}

/** The 390 px wizard frame of the phone screens (Admin M1–M4, Nutzer M1–M9) at full viewport size. */
export function MobileWizardFrame({
  title,
  stepLabel,
  segments = 0,
  current = 0,
  onBack,
  footer,
  children,
}: MobileWizardFrameProps) {
  return (
    <div className={styles.frame}>
      <header className={styles.head}>
        <div className={styles.headRow}>
          {onBack ? (
            <button type="button" className={styles.back} onClick={onBack} aria-label="Zurück">
              ‹
            </button>
          ) : null}
          <h1 className={styles.title}>{title}</h1>
          {stepLabel ? <span className={styles.stepLabel}>{stepLabel}</span> : null}
        </div>
        {segments > 0 ? (
          <div
            className={styles.segments}
            role="progressbar"
            aria-label={stepLabel ?? title}
            aria-valuemin={0}
            aria-valuemax={segments}
            aria-valuenow={Math.min(current, segments)}
          >
            {Array.from({ length: segments }, (_, index) => (
              <span key={index} className={clsx(styles.segment, index < current && styles.segmentOn)} />
            ))}
          </div>
        ) : null}
      </header>
      <main className={styles.body}>
        {children}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </main>
    </div>
  );
}
